import Component from '@glimmer/component';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { fn, array, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { or, and, not, eq } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { variation } from 'ember-launch-darkly';
import set from 'ember-set-helper/helpers/set';
import can from 'ember-can/helpers/can';
import * as Sentry from '@sentry/ember';
import {
  HdsModal,
  HdsAlert,
  HdsTextBody,
  HdsLinkStandalone,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import {
  Flex,
  CoreDropDownGoToTerraform,
  ModalConfirm,
  WithErrors,
} from 'core/components';
import { supportTicketPrefillHref } from 'core/utils/support-ticket';
import {
  ACTIVE_RESOURCES_PROJECT_DELETE_ERROR,
  ACTIVE_RESOURCES_ORIGIN_PROJECT_DELETE,
} from 'hcp/utils/constants';

/**
 *
 * A modal tied to the MutationContaier's context and yielded by its parent component.
 * Conditionally renders modals for every implemented mutation method (API, CRUD).
 * Currently only exposes the Delete Project modal and its enabled/disabled state.
 *
 *
 * ```
 * <Page::Projects::MutationContainer::Modals
 *  @modals={{this.modals}}
 *  @showDeleteModal={{this.showDeleteModal}}
 *  @resource={{this.resourceToMutate}}
 *  @onClose={{this.hideModalByType}}
 * />
 * ```
 *
 * @class PageProjectsMutationContainerModals
 *
 */
export default class PageProjectsMutationContainerModals extends Component {
  @service flashMessages;
  @service intl;
  @service router;
  @service userContext;

  /**
   * A map of modal state.
   * @argument modals
   * @type {boolean}
   */
  /**
   * The resource model object targeted for any mutation.
   * @argument resource
   * @type {Object}
   */
  /**
   * A callback to handle conditional render state
   * @argument onClose
   * @type {Function}
   */

  projectDeleteContentOrigin = ACTIVE_RESOURCES_ORIGIN_PROJECT_DELETE;
  ACTIVE_RESOURCES_PROJECT_DELETE_ERROR = ACTIVE_RESOURCES_PROJECT_DELETE_ERROR;

  get supportTicketHref() {
    return supportTicketPrefillHref(this.userContext);
  }

  deleteProject = task(async (projectId) => {
    const { service } = this.args;

    await service.delete.task.perform(projectId);

    this.flashMessages.success(
      this.intl.t('components.page.projects.create.project-deleted')
    );

    const redirectRoute = 'cloud.orgs.detail.projects.list';
    const container = getOwner(this);
    const route = container.lookup(`route:${redirectRoute}`);
    route.refresh();

    // Unset this project if it's currently set and navigate back to the list.
    if (this.userContext?.project?.id === projectId) {
      this.userContext.setProject(null);
    }

    this.router.transitionTo(`${redirectRoute}`);
  });

  onClose = (action) => {
    const { onClose } = this.args;
    if (onClose && typeof onClose === 'function') {
      onClose(action);
    }
    // Clean up last successful/error task instances
    this.deleteProject.cancelAll({ resetState: true });
  };

  onSubmit = async (projectId, e) => {
    e.preventDefault();
    try {
      await this.deleteProject.perform(projectId);
      this.onClose('delete');
    } catch (error) {
      if (macroCondition(isDevelopingApp())) {
        console.error(error);
      } else {
        Sentry.captureException(error);
      }
    }
  };

  <template>
    {{#if (and @service.delete @modals.delete.isVisible)}}
      {{!
  The delete button in actions dropdown should be hidden when an organization
  has one or less projects. Just in case that is bypassed, this pops a modal
  that displays a message indicating that they cannot delete this project.
  }}
      {{#if
        (can
          "view confirm delete modal in project"
          @service.projects
          allowedPermissions=@resource.allowedPermissions
        )
      }}
        {{#if
          (can
            "delete empty project"
            @resource
            allowedPermissions=@resource.allowedPermissions
          )
        }}
          <WithErrors
            @error={{this.deleteProject.last.error}}
            as |FormError FieldError error|
          >
            <HdsModal
              @color="critical"
              @onClose={{fn this.onClose "delete"}}
              data-test-project-delete-confirm-modal
              as |M|
            >
              <M.Header @icon="alert-diamond" @tagline={{@resource.name}}>
                {{t "components.page.projects.settings.modal.delete.title"}}
              </M.Header>
              <M.Body>
                <Flex @alignItems="flex-start" @direction="column" @gap="md">
                  {{#if @unifiedExperienceEnabled}}
                    {{#if error}}
                      {{#if
                        (and
                          (eq
                            error.status
                            this.ACTIVE_RESOURCES_PROJECT_DELETE_ERROR.STATUS
                          )
                          (eq
                            error.message
                            this.ACTIVE_RESOURCES_PROJECT_DELETE_ERROR.MESSAGE
                          )
                        )
                      }}
                        <HdsAlert
                          @type="inline"
                          @color="critical"
                          data-test-active-resources-project-delete-error
                          as |A|
                        >
                          <A.Title>
                            {{t
                              "components.page.projects.settings.modal.delete.error.active-tfc-resources.title"
                            }}
                          </A.Title>
                          <A.Description>
                            <Flex
                              @alignItems="flex-start"
                              @direction="column"
                              @gap="md"
                            >
                              <HdsTextBody>
                                {{t
                                  "components.page.projects.settings.modal.delete.error.active-tfc-resources.body"
                                  htmlSafe=true
                                  projectName=@resource.name
                                }}
                              </HdsTextBody>
                              <Flex
                                @alignItems="center"
                                @direction="row"
                                @gap="md"
                                data-test-hcp-project-delete-error-controls
                              >
                                <CoreDropDownGoToTerraform
                                  data-test-hcp-project-delete-error-go-to-terraform-dropdown
                                />
                                <HdsLinkStandalone
                                  @icon="help"
                                  @iconPosition="leading"
                                  @href={{this.supportTicketHref}}
                                  @size="small"
                                  @text={{t
                                    "components.page.projects.settings.modal.delete.error.support"
                                  }}
                                  data-test-hcp-project-delete-error-support
                                />
                              </Flex>
                            </Flex>
                          </A.Description>
                        </HdsAlert>
                      {{else}}
                        <FormError data-test-project-delete-error />
                      {{/if}}
                    {{else}}
                      <HdsAlert @type="compact" @color="highlight" as |A|>
                        <A.Description>
                          {{t
                            "components.page.projects.settings.modal.delete.alert"
                            htmlSafe=true
                            projectName=@resource.name
                          }}
                        </A.Description>
                      </HdsAlert>
                    {{/if}}
                  {{else}}
                    {{#if error}}
                      <FormError data-test-project-delete-error />
                    {{/if}}
                  {{/if}}
                  <HdsTextBody>
                    {{t
                      "components.page.projects.settings.modal.delete.body"
                      htmlSafe=true
                      projectName=@resource.name
                    }}
                  </HdsTextBody>
                  <form
                    id="{{@service.type}}-delete"
                    class="hcp-hds-form"
                    {{on "submit" (fn this.onSubmit @resource.id)}}
                  >
                    <ModalConfirm
                      @onAllowedChange={{set this "allowDelete"}}
                      @confirmLabel={{t
                        "components.page.projects.settings.modal.delete.label"
                      }}
                      @confirmHelp={{t
                        "components.page.projects.settings.modal.delete.helper-text"
                        htmlSafe=true
                        projectName=@resource.name
                      }}
                    />
                  </form>
                </Flex>
              </M.Body>
              <M.Footer as |F|>
                <HdsButtonSet>
                  <HdsButton
                    @color="critical"
                    @text={{t
                      "components.page.projects.settings.modal.delete.action"
                    }}
                    disabled={{or
                      (not this.allowDelete)
                      @service.delete.task.isRunning
                      (not (variation "hcp-project-delete-enabled"))
                    }}
                    form="{{@service.type}}-delete"
                    type="submit"
                    data-test-confirm-delete
                  />
                  <HdsButton
                    @color="secondary"
                    @text={{t "components.form.cancel"}}
                    {{on "click" F.close}}
                  />
                </HdsButtonSet>
              </M.Footer>
            </HdsModal>
          </WithErrors>
        {{else}}
          {{! Unable to delete project modal due to existing active resources }}
          <HdsModal
            @color="warning"
            @onClose={{fn this.onClose "delete"}}
            data-test-project-unable-to-delete-modal
            as |M|
          >
            <M.Header @icon="alert-triangle" @tagline={{@resource.name}}>
              {{t
                "components.page.projects.settings.modal.unable-to-delete.title"
              }}
            </M.Header>
            <M.Body>
              <Flex @alignItems="center" @direction="column" @gap="md">
                <HdsTextBody>
                  {{t
                    "components.page.projects.settings.modal.unable-to-delete.body"
                    htmlSafe=true
                    projectName=@resource.name
                  }}
                </HdsTextBody>
                {{#if @unifiedExperienceEnabled}}
                  <HdsAlert @type="compact" @color="highlight" as |A|>
                    <A.Description>
                      {{t
                        "components.page.projects.settings.modal.unable-to-delete.alert"
                        htmlSafe=true
                        projectName=@resource.name
                      }}
                    </A.Description>
                  </HdsAlert>
                {{/if}}
              </Flex>
            </M.Body>
            <M.Footer as |F|>
              <HdsButtonSet>
                <HdsButton
                  @color="primary"
                  @icon="arrow-right"
                  @iconPosition="trailing"
                  @text={{t
                    "components.page.projects.settings.modal.unable-to-delete.action"
                  }}
                  @route="cloud.orgs.detail.projects.detail.active-resources"
                  @models={{array @resource.id}}
                  @query={{if
                    @unifiedExperienceEnabled
                    (hash
                      activeResourcesListOrigin=this.projectDeleteContentOrigin
                    )
                  }}
                  data-test-delete-project-modal-view-active-resources
                />
                <HdsButton
                  @color="secondary"
                  @text={{t "components.form.cancel"}}
                  data-test-delete-project-modal-close
                  {{on "click" F.close}}
                />
              </HdsButtonSet>
            </M.Footer>
          </HdsModal>
        {{/if}}
      {{else}}
        <HdsModal
          @color="warning"
          @onClose={{fn this.onClose "delete"}}
          data-test-project-not-enough-projects-modal
          as |M|
        >
          <M.Header @icon="info" @tagline={{@resource.name}}>
            {{t
              "components.page.projects.settings.modal.not-enough-projects.title"
            }}
          </M.Header>
          <M.Body>
            {{t
              "components.page.projects.settings.modal.not-enough-projects.body"
            }}
          </M.Body>
          <M.Footer as |F|>
            <HdsButton
              @color="secondary"
              @text={{t "components.form.cancel"}}
              data-test-delete-project-modal-close
              {{on "click" F.close}}
            />
          </M.Footer>
        </HdsModal>
      {{/if}}
    {{/if}}
  </template>
}
